import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import DefaultlayoutHOC from '../layouts/Default.layout';
import CatalogListRow from '../components/catalog/CatalogListRow.component';
import InfiniteFeed from '../components/InfiniteFeed/InfiniteFeed.component';
import useInfiniteFeed from '../hooks/useInfiniteFeed';
import { movieFeed, MOVIE_SORT_OPTIONS } from '../services/tmdbFeed';
import { FaFilter, FaSortAmountDown, FaTh, FaList, FaUndo } from 'react-icons/fa';

const GENRES = [
  { id: 'all', name: 'All Genres' },
  { id: 28, name: 'Action' },
  { id: 12, name: 'Adventure' },
  { id: 878, name: 'Sci-Fi' },
  { id: 18, name: 'Drama' },
  { id: 35, name: 'Comedy' },
  { id: 27, name: 'Horror' },
  { id: 16, name: 'Animation' },
  { id: 80, name: 'Crime' },
  { id: 53, name: 'Thriller' },
];

const RATING_OPTIONS = [0, 7, 8, 8.5];

const MoviesPage = () => {
  const [searchParams] = useSearchParams();

  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || 'all');
  const [selectedSort, setSelectedSort] = useState('newest');
  const [minRating, setMinRating] = useState(0);
  const [viewMode, setViewMode] = useState('grid');

  // Navbar deep-links to /movies?genre=<id>; keep the feed in sync when the user
  // arrives that way while the page is already mounted.
  const genreParam = searchParams.get('genre');
  useEffect(() => {
    if (genreParam) setSelectedGenre(genreParam);
  }, [genreParam]);

  // Sorting + genre + rating all go to TMDB, so every appended page stays in the
  // same order as the pages already on screen.
  const feed = useInfiniteFeed({
    kind: 'movie',
    fetcher: movieFeed.fetch,
    sort: selectedSort,
    genreId: selectedGenre,
    minRating,
  });

  const resetFilters = () => {
    setSelectedGenre('all');
    setSelectedSort('newest');
    setMinRating(0);
  };

  const isFiltered = selectedGenre !== 'all' || selectedSort !== 'newest' || minRating > 0;

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header Banner */}
      <div className="mb-8 bg-gradient-to-r from-dark-800 via-dark-800 to-dark-700 border border-white/10 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
        <div className="max-w-2xl relative z-10">
          <span className="bg-accent-gold text-dark-900 font-extrabold text-xs px-3 py-1 rounded-full uppercase tracking-wider mb-3 inline-block">
            Cinematic Library
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-2 tracking-tight">
            Movies Catalog &amp; Discover
          </h1>
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
            Bollywood, South Indian (Tamil / Telugu / Kannada / Malayalam), Hollywood in
            Hindi &amp; Gujarati — newest releases first, with more loading as you scroll.
            Filter by genre and rating or re-sort the whole catalog.
          </p>
        </div>
      </div>

      {/* Control Bar: Filters & Sorter */}
      <div className="bg-dark-800/90 rounded-2xl border border-white/10 p-4 sm:p-5 mb-8 shadow-xl space-y-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          {/* Genre Selection Chips */}
          <div className="flex items-center gap-2 overflow-x-auto w-full lg:w-auto pb-2 lg:pb-0 scrollbar-hide">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mr-1 flex-shrink-0">
              <FaFilter className="text-accent-gold w-3 h-3" aria-hidden="true" />
              Genre:
            </span>
            {GENRES.map((genre) => (
              <button
                key={genre.id}
                type="button"
                onClick={() => setSelectedGenre(String(genre.id))}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${
                  String(selectedGenre) === String(genre.id)
                    ? 'bg-accent-gold text-dark-900 border-accent-gold font-bold shadow-md shadow-accent-gold/20'
                    : 'bg-dark-700 text-gray-300 border-white/10 hover:border-accent-gold/40 hover:text-white'
                }`}
              >
                {genre.name}
              </button>
            ))}
          </div>

          {/* Right Controls: Sort & View Mode */}
          <div className="flex items-center gap-3 w-full lg:w-auto justify-between lg:justify-end">
            <div className="flex items-center gap-2">
              <FaSortAmountDown className="text-accent-gold w-3.5 h-3.5" aria-hidden="true" />
              <select
                aria-label="Sort movies"
                value={selectedSort}
                onChange={(e) => setSelectedSort(e.target.value)}
                className="bg-dark-700 text-white text-xs font-medium border border-white/10 rounded-xl px-3 py-2 focus:outline-none focus:border-accent-gold/50 cursor-pointer"
              >
                {MOVIE_SORT_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id} className="bg-dark-800 text-white">
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center bg-dark-700 rounded-xl p-1 border border-white/10">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                className={`p-1.5 rounded-lg transition-colors ${
                  viewMode === 'grid' ? 'bg-accent-gold text-dark-900' : 'text-gray-400 hover:text-white'
                }`}
                title="Grid View"
              >
                <FaTh className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                className={`p-1.5 rounded-lg transition-colors ${
                  viewMode === 'list' ? 'bg-accent-gold text-dark-900' : 'text-gray-400 hover:text-white'
                }`}
                title="List View"
              >
                <FaList className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Secondary Filter Row: Rating Filter & Reset */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/5 pt-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-medium">Minimum Rating:</span>
            {RATING_OPTIONS.map((val) => (
              <button
                key={val}
                type="button"
                onClick={() => setMinRating(val)}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${
                  minRating === val
                    ? 'bg-amber-500/20 text-accent-gold border-accent-gold font-bold'
                    : 'bg-dark-700 text-gray-400 border-white/5 hover:text-white'
                }`}
              >
                {val === 0 ? 'All' : `${val}+ ★`}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>
              Loaded <strong className="text-white">{feed.items.length}</strong> of{' '}
              <strong className="text-white">{feed.totalResults.toLocaleString()}</strong> titles
            </span>
            {isFiltered && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 text-accent-gold hover:underline font-semibold ml-2"
              >
                <FaUndo className="w-2.5 h-2.5" aria-hidden="true" />
                <span>Reset Filters</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <InfiniteFeed
        items={feed.items}
        status={feed.status}
        error={feed.error}
        hasMore={feed.hasMore}
        totalResults={feed.totalResults}
        isInitialLoading={feed.isInitialLoading}
        isLoadingMore={feed.isLoadingMore}
        onLoadMore={feed.loadMore}
        onRetry={feed.retry}
        label="Movies catalog"
        noun="movies"
        skeletonCount={viewMode === 'grid' ? 12 : 4}
        gridClassName={
          viewMode === 'grid'
            ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6'
            : 'space-y-4'
        }
        renderItem={
          viewMode === 'list'
            ? (movie) => (
                <CatalogListRow item={movie} mediaType="movie" detailPath={`/movie/${movie.id}`} />
              )
            : undefined
        }
        emptyState={
          <div className="text-center py-20 bg-dark-800/40 rounded-3xl border border-white/5 p-8">
            <p className="text-gray-300 text-base font-semibold mb-2">
              No movies match your selected filters
            </p>
            <p className="text-gray-500 text-xs sm:text-sm mb-6">
              Try adjusting the genre filter, sorting, or lowering the minimum rating requirement.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-bold text-xs px-5 py-2.5 rounded-xl transition-all shadow-md"
            >
              Reset All Filters
            </button>
          </div>
        }
      />

      {/* Deep-link hint for TV/browse entry points */}
      <p className="text-center text-xs text-gray-600 mt-10">
        Looking for unreleased Indian titles?{' '}
        <Link to="/" className="text-accent-gold hover:underline">
          See the Upcoming lane on Home.
        </Link>
      </p>
    </div>
  );
};

export default DefaultlayoutHOC(MoviesPage);
